import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Lock, LogOut, Settings, Users, BarChart3, Save, RefreshCw, Eye, EyeOff,
  TrendingUp, Calendar, Mail, Phone, MapPin, Car, ChevronRight, Loader2,
  Building2, User, ExternalLink, Search, Trash2, FileText, CheckCircle, XCircle,
  BarChart2, Percent, MessageSquare, Plus, Image, Upload, X, Star
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { RichEditor } from "../components/RichEditor";

const envBackend = process.env.REACT_APP_BACKEND_URL;
const API = envBackend ? `${envBackend}/api` : (window.APP_CONFIG?.API_URL || '/api');

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [leads, setLeads] = useState([]);
  const [searches, setSearches] = useState([]);
  const [stats, setStats] = useState({ 
    total_leads: 0, 
    new_leads: 0, 
    recent_leads: 0,
    total_searches: 0,
    recent_searches: 0,
    successful_scrapes: 0,
    conversion_rate: 0
  });
  const [settings, setSettings] = useState({
    transport_cost: 720,
    inspection_origin_cost: 180,
    itv_cost: 90,
    gestoria_dgt_cost: 110,
    commission_brackets: [
      { min: 0, max: 15000, commission: 1400 },
      { min: 15001, max: 30000, commission: 1600 },
      { min: 30001, max: 50000, commission: 1800 },
      { min: 50001, max: null, commission: 2000 }
    ],
    negotiation_discount_percent: 2,
    spain_markup_percent: 11.8,
    co2_brackets: {
      "0-120": 0,
      "121-159": 4.75,
      "160-199": 9.75,
      "200+": 14.75
    }
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [mktVehicles, setMktVehicles] = useState([]);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: "", excerpt: "", content: "", cover_image: "", tags: "", published: false });
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: "", text: "", stars: 5, photo: "" });
  const [vehicleForm, setVehicleForm] = useState({
    title: "", make: "", model: "", year: 2024, kilometers: 0,
    fuel_type: "Gasolina", power_cv: 0, price: 0, color: "", location: "", notes: ""
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem("admin_token");
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchData();
    }
  }, [isLoggedIn, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [leadsRes, searchesRes, statsRes, settingsRes, messagesRes, vehiclesRes, blogRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/admin/searches`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }).catch(() => ({ data: { messages: [] } })),
        axios.get(`${API}/admin/vehicles`, { headers }).catch(() => ({ data: { vehicles: [] } })),
        axios.get(`${API}/admin/blog`, { headers }).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/reviews`).catch(() => ({ data: { reviews: [] } }))
      ]);
      setLeads(leadsRes.data.leads || []);
      setSearches(searchesRes.data.searches || []);
      setStats(statsRes.data.stats || {});
      setSettings(settingsRes.data.settings || settings);
      setMessages(messagesRes.data.messages || []);
      setMktVehicles(vehiclesRes.data.vehicles || []);
      setBlogPosts(blogRes.data.posts || []);
      setReviews(reviewsRes.data.reviews || []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Sesión expirada");
      }
    }
  };

  const handleLogin = async () => {
    if (!credentials.username || !credentials.password) {
      toast.error("Introduce usuario y contraseña");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const { token: newToken } = response.data;
      setToken(newToken);
      localStorage.setItem("admin_token", newToken);
      setIsLoggedIn(true);
      toast.success("Sesión iniciada correctamente");
    } catch (error) {
      toast.error("Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("admin_token");
    setIsLoggedIn(false);
    setCredentials({ username: "", password: "" });
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success("Estado actualizado");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const updateLeadNotes = async (leadId, notes) => {
    try {
      await axios.put(`${API}/leads/${leadId}/notes?notes=${encodeURIComponent(notes)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success("Notas guardadas");
    } catch (error) {
      toast.error("Error al guardar notas");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("¿Eliminar este lead?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLead(null);
      fetchData();
      toast.success("Lead eliminado");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const deleteSearch = async (searchId) => {
    if (!window.confirm("¿Eliminar esta búsqueda?")) return;
    try {
      await axios.delete(`${API}/admin/searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSearch(null);
      fetchData();
      toast.success("Búsqueda eliminada");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const deleteAllLeads = async () => {
    if (!window.confirm("¿Eliminar TODOS los leads? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      toast.success("Todos los leads eliminados");
    } catch { toast.error("Error al eliminar"); }
  };

  const deleteAllSearches = async () => {
    if (!window.confirm("¿Eliminar TODAS las búsquedas? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/searches`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      toast.success("Todas las búsquedas eliminadas");
    } catch { toast.error("Error al eliminar"); }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30",
      contacted: "bg-[#FF9500]/20 text-[#FF9500] border-[#FF9500]/30",
      negotiating: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      closed: "bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30",
      lost: "bg-gray-500/20 text-gray-400 border-gray-500/30"
    };
    const labels = {
      new: "Nuevo",
      contacted: "Contactado",
      negotiating: "En negociación",
      closed: "Cerrado",
      lost: "Perdido"
    };
    return (
      <Badge className={`${styles[status] || styles.new} border`}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#111111] border-[rgba(138,138,141,0.2)]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF3B30] to-[#CC2F26] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Panel de Administración</CardTitle>
            <p className="text-[#8A8A8D] text-sm mt-2">Acceso restringido</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#C4C4C6] text-sm">Usuario</Label>
              <Input
                placeholder="admin"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                className="glass-input mt-1"
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <Label className="text-[#C4C4C6] text-sm">Contraseña</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="glass-input pr-10"
                  data-testid="admin-password-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8D] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={handleLogin}
              disabled={loading}
              className="btn-red w-full h-12"
              data-testid="admin-login-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ChevronRight className="w-5 h-5 mr-2" />}
              ACCEDER
            </Button>
            <p className="text-center text-[#8A8A8D] text-xs">
              Acceso exclusivo para administradores
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#111111] border-b border-[rgba(138,138,141,0.15)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF3B30] to-[#CC2F26] flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">TE LO BAJO</h1>
              <p className="text-xs text-[#8A8A8D]">Panel Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchData}
              variant="ghost"
              size="icon"
              className="text-[#8A8A8D] hover:text-white"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-[#8A8A8D] hover:text-white"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]" data-testid="stat-total">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#8A8A8D] text-xs">Total Leads</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats.total_leads}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#FF3B30]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]" data-testid="stat-new">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#8A8A8D] text-xs">Leads Nuevos</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats.new_leads}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#34C759]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]" data-testid="stat-searches">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#8A8A8D] text-xs">Total Búsquedas</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats.total_searches}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Search className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]" data-testid="stat-scrapes">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#8A8A8D] text-xs">Scraping OK</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats.successful_scrapes}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#34C759]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]" data-testid="stat-recent">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#8A8A8D] text-xs">Últimos 7 días</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats.recent_leads}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]" data-testid="stat-conversion">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#8A8A8D] text-xs">Conversión</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats.conversion_rate}%</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#FF9500]/10 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-[#FF9500]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="bg-[#111111] border border-[rgba(138,138,141,0.15)] p-1">
            <TabsTrigger 
              value="leads" 
              className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
              data-testid="tab-leads"
            >
              <Users className="w-4 h-4 mr-2" />
              Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger 
              value="searches" 
              className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
              data-testid="tab-searches"
            >
              <Search className="w-4 h-4 mr-2" />
              Búsquedas ({searches.length})
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
              data-testid="tab-settings"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </TabsTrigger>
            <TabsTrigger 
              value="messages"
              className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white relative"
              data-testid="tab-messages"
            >
              <Mail className="w-4 h-4 mr-2" />
              Mensajes
              {messages.filter(m => !m.read).length > 0 && (
                <span className="ml-2 bg-[#FF3B30] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {messages.filter(m => !m.read).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="vehicles"
              className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
              data-testid="tab-vehicles"
            >
              <Car className="w-4 h-4 mr-2" />
              Vehículos ({mktVehicles.length})
            </TabsTrigger>
            <TabsTrigger 
              value="blog"
              className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-white"
              data-testid="tab-blog"
            >
              <FileText className="w-4 h-4 mr-2" />
              Blog ({blogPosts.length})
            </TabsTrigger>
            <TabsTrigger 
              value="reviews"
              className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-white"
              data-testid="tab-reviews"
            >
              <Star className="w-4 h-4 mr-2" />
              Reseñas ({reviews.length})
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FF3B30]" />
                  Clientes Potenciales (Leads)
                </CardTitle>
                {leads.length > 0 && (
                  <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" onClick={deleteAllLeads} data-testid="delete-all-leads">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar todos
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <div className="text-center py-12 text-[#8A8A8D]">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay leads registrados aún</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[rgba(138,138,141,0.15)] hover:bg-transparent">
                          <TableHead className="text-[#8A8A8D]">Fecha</TableHead>
                          <TableHead className="text-[#8A8A8D]">Nombre</TableHead>
                          <TableHead className="text-[#8A8A8D]">Contacto</TableHead>
                          <TableHead className="text-[#8A8A8D]">Provincia</TableHead>
                          <TableHead className="text-[#8A8A8D]">Vehículo</TableHead>
                          <TableHead className="text-[#8A8A8D]">Total</TableHead>
                          <TableHead className="text-[#8A8A8D]">Estado</TableHead>
                          <TableHead className="text-[#8A8A8D]">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow 
                            key={lead.id} 
                            className="border-[rgba(138,138,141,0.15)] hover:bg-[#1A1A1A] cursor-pointer"
                            onClick={() => setSelectedLead(lead)}
                            data-testid={`lead-row-${lead.id}`}
                          >
                            <TableCell className="text-[#C4C4C6] text-sm">
                              {formatDate(lead.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {lead.buyer_type === "empresa" ? (
                                  <Building2 className="w-4 h-4 text-[#8A8A8D]" />
                                ) : (
                                  <User className="w-4 h-4 text-[#8A8A8D]" />
                                )}
                                <span className="text-white font-medium">{lead.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center gap-1 text-[#C4C4C6] text-sm">
                                  <Mail className="w-3 h-3" />
                                  {lead.email}
                                </div>
                                <div className="flex items-center gap-1 text-[#8A8A8D] text-xs">
                                  <Phone className="w-3 h-3" />
                                  {lead.phone}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-[#C4C4C6]">
                                <MapPin className="w-3 h-3 text-[#8A8A8D]" />
                                {lead.province}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-[#C4C4C6] text-sm max-w-[200px] truncate">
                                {lead.vehicle_data?.title || `${lead.vehicle_data?.year || ''}`}
                              </div>
                              <div className="text-[#FF3B30] font-medium text-sm">
                                {formatPrice(lead.vehicle_data?.price || 0)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-white font-bold">
                                {formatPrice(lead.calculated_result?.total || 0)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={lead.status || "new"} 
                                onValueChange={(value) => updateLeadStatus(lead.id, value)}
                              >
                                <SelectTrigger className="w-32 h-8 bg-transparent border-0 p-0">
                                  {getStatusBadge(lead.status || "new")}
                                </SelectTrigger>
                                <SelectContent className="bg-[#1A1A1A] border-[rgba(138,138,141,0.2)]">
                                  {["new", "contacted", "negotiating", "closed", "lost"].map(status => (
                                    <SelectItem key={status} value={status} className="text-white hover:bg-[#252525]">
                                      {getStatusBadge(status)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-[#8A8A8D] hover:text-white"
                                  onClick={() => setSelectedLead(lead)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-[#8A8A8D] hover:text-[#FF3B30]"
                                  onClick={() => deleteLead(lead.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Searches Tab */}
          <TabsContent value="searches">
            <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#FF3B30]" />
                  Historial de Búsquedas
                </CardTitle>
                {searches.length > 0 && (
                  <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" onClick={deleteAllSearches} data-testid="delete-all-searches">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar todas
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {searches.length === 0 ? (
                  <div className="text-center py-12 text-[#8A8A8D]">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay búsquedas registradas aún</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[rgba(138,138,141,0.15)] hover:bg-transparent">
                          <TableHead className="text-[#8A8A8D]">Fecha</TableHead>
                          <TableHead className="text-[#8A8A8D]">URL mobile.de</TableHead>
                          <TableHead className="text-[#8A8A8D]">Vehículo Detectado</TableHead>
                          <TableHead className="text-[#8A8A8D]">Precio</TableHead>
                          <TableHead className="text-[#8A8A8D]">Scraping</TableHead>
                          <TableHead className="text-[#8A8A8D]">Conversión</TableHead>
                          <TableHead className="text-[#8A8A8D]">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searches.map((search) => (
                          <TableRow 
                            key={search.id} 
                            className="border-[rgba(138,138,141,0.15)] hover:bg-[#1A1A1A]"
                            data-testid={`search-row-${search.id}`}
                          >
                            <TableCell className="text-[#C4C4C6] text-sm">
                              {formatDate(search.created_at)}
                            </TableCell>
                            <TableCell>
                              <a 
                                href={search.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#FF3B30] hover:underline flex items-center gap-1 text-sm max-w-[250px] truncate"
                              >
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                {search.url.substring(0, 50)}...
                              </a>
                            </TableCell>
                            <TableCell>
                              <div className="text-white text-sm max-w-[200px] truncate">
                                {search.vehicle_data?.title || search.vehicle_data?.make || 'No detectado'}
                              </div>
                              {search.vehicle_data?.year > 0 && (
                                <div className="text-[#8A8A8D] text-xs">
                                  {search.vehicle_data.year} • {search.vehicle_data.kilometers?.toLocaleString() || 0} km
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              {search.vehicle_data?.price > 0 ? (
                                <span className="text-[#FF3B30] font-medium">
                                  {formatPrice(search.vehicle_data.price)}
                                </span>
                              ) : (
                                <span className="text-[#8A8A8D]">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {search.scrape_success ? (
                                <Badge className="bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30 border">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  OK
                                </Badge>
                              ) : (
                                <Badge className="bg-[#FF9500]/20 text-[#FF9500] border-[#FF9500]/30 border">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Manual
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {search.converted_to_lead ? (
                                <Badge className="bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30 border">
                                  Lead
                                </Badge>
                              ) : (
                                <span className="text-[#8A8A8D] text-sm">No</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-[#8A8A8D] hover:text-white"
                                  onClick={() => setSelectedSearch(search)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-[#8A8A8D] hover:text-[#FF3B30]"
                                  onClick={() => deleteSearch(search.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#FF3B30]" />
                  Configuración de Costes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Fixed Costs */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Costes Fijos</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">Transporte (€)</Label>
                      <Input
                        type="number"
                        value={settings.transport_cost}
                        onChange={(e) => setSettings({...settings, transport_cost: parseFloat(e.target.value)})}
                        className="glass-input mt-1"
                        data-testid="settings-transport"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">Inspección en Origen (€)</Label>
                      <Input
                        type="number"
                        value={settings.inspection_origin_cost}
                        onChange={(e) => setSettings({...settings, inspection_origin_cost: parseFloat(e.target.value)})}
                        className="glass-input mt-1"
                        data-testid="settings-inspection"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">Revisión ITV (€)</Label>
                      <Input
                        type="number"
                        value={settings.itv_cost}
                        onChange={(e) => setSettings({...settings, itv_cost: parseFloat(e.target.value)})}
                        className="glass-input mt-1"
                        data-testid="settings-itv"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">Gestoría + Tasas DGT (€)</Label>
                      <Input
                        type="number"
                        value={settings.gestoria_dgt_cost}
                        onChange={(e) => setSettings({...settings, gestoria_dgt_cost: parseFloat(e.target.value)})}
                        className="glass-input mt-1"
                        data-testid="settings-dgt"
                      />
                    </div>
                    
                    {/* Commission brackets */}
                    <div className="col-span-2">
                      <Label className="text-[#C4C4C6] text-sm mb-3 block">Comisión Te Lo Bajo por tramos</Label>
                      <div className="space-y-2">
                        {(settings.commission_brackets || []).map((bracket, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-[#1C1C1E] p-3 rounded-lg border border-[rgba(138,138,141,0.1)]">
                            <div className="flex items-center gap-1 flex-1">
                              <span className="text-[#8A8A8D] text-xs whitespace-nowrap">Desde</span>
                              <Input
                                type="number"
                                value={bracket.min}
                                onChange={(e) => {
                                  const updated = [...settings.commission_brackets];
                                  updated[idx] = { ...updated[idx], min: parseFloat(e.target.value) || 0 };
                                  setSettings({ ...settings, commission_brackets: updated });
                                }}
                                className="glass-input h-8 text-sm w-24"
                                data-testid={`commission-min-${idx}`}
                              />
                              <span className="text-[#8A8A8D] text-xs whitespace-nowrap">hasta</span>
                              {bracket.max !== null ? (
                                <Input
                                  type="number"
                                  value={bracket.max}
                                  onChange={(e) => {
                                    const updated = [...settings.commission_brackets];
                                    updated[idx] = { ...updated[idx], max: parseFloat(e.target.value) || 0 };
                                    setSettings({ ...settings, commission_brackets: updated });
                                  }}
                                  className="glass-input h-8 text-sm w-24"
                                  data-testid={`commission-max-${idx}`}
                                />
                              ) : (
                                <span className="text-[#C4C4C6] text-sm font-medium w-24 text-center">Sin límite</span>
                              )}
                              <span className="text-[#8A8A8D] text-xs">€</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[#8A8A8D] text-xs">=</span>
                              <Input
                                type="number"
                                value={bracket.commission}
                                onChange={(e) => {
                                  const updated = [...settings.commission_brackets];
                                  updated[idx] = { ...updated[idx], commission: parseFloat(e.target.value) || 0 };
                                  setSettings({ ...settings, commission_brackets: updated });
                                }}
                                className="glass-input h-8 text-sm w-24"
                                data-testid={`commission-val-${idx}`}
                              />
                              <span className="text-[#FF3B30] text-xs font-medium">€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">Descuento Negociación (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={settings.negotiation_discount_percent}
                        onChange={(e) => setSettings({...settings, negotiation_discount_percent: parseFloat(e.target.value)})}
                        className="glass-input mt-1"
                        data-testid="settings-discount"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">Margen España (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={settings.spain_markup_percent}
                        onChange={(e) => setSettings({...settings, spain_markup_percent: parseFloat(e.target.value)})}
                        className="glass-input mt-1"
                        data-testid="settings-spain-markup"
                      />
                      <p className="text-[#636366] text-xs mt-1">% sobre precio final para calcular precio en España</p>
                    </div>
                  </div>
                </div>

                {/* CO2 Brackets - IEDMT */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Tramos IEDMT - Impuesto Matriculación (%)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">0-120 g/km (Exento)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.co2_brackets?.["0-120"] || 0}
                        onChange={(e) => setSettings({
                          ...settings, 
                          co2_brackets: {...settings.co2_brackets, "0-120": parseFloat(e.target.value)}
                        })}
                        className="glass-input mt-1"
                        data-testid="settings-co2-0"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">121-159 g/km</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.co2_brackets?.["121-159"] ?? settings.co2_brackets?.["121-160"] ?? 4.75}
                        onChange={(e) => setSettings({
                          ...settings, 
                          co2_brackets: {...settings.co2_brackets, "121-159": parseFloat(e.target.value)}
                        })}
                        className="glass-input mt-1"
                        data-testid="settings-co2-121"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">160-199 g/km</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.co2_brackets?.["160-199"] ?? settings.co2_brackets?.["161-200"] ?? 9.75}
                        onChange={(e) => setSettings({
                          ...settings, 
                          co2_brackets: {...settings.co2_brackets, "160-199": parseFloat(e.target.value)}
                        })}
                        className="glass-input mt-1"
                        data-testid="settings-co2-161"
                      />
                    </div>
                    <div>
                      <Label className="text-[#C4C4C6] text-sm">&ge;200 g/km</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.co2_brackets?.["200+"] ?? settings.co2_brackets?.["201+"] ?? 14.75}
                        onChange={(e) => setSettings({
                          ...settings, 
                          co2_brackets: {...settings.co2_brackets, "200+": parseFloat(e.target.value)}
                        })}
                        className="glass-input mt-1"
                        data-testid="settings-co2-201"
                      />
                    </div>
                  </div>
                </div>

                {/* Webhook Config */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Integración Zapier</h3>
                  <div className="bg-[#1A1A1A] rounded-xl p-4 border border-[rgba(138,138,141,0.1)]">
                    <p className="text-[#8A8A8D] text-sm mb-2">
                      Endpoint para recibir webhooks:
                    </p>
                    <code className="text-[#FF3B30] text-sm bg-[#0A0A0A] px-3 py-2 rounded block">
                      {process.env.REACT_APP_BACKEND_URL}/api/webhook
                    </code>
                    <p className="text-[#8A8A8D] text-xs mt-3">
                      Configura este endpoint en Zapier para recibir notificaciones de nuevos leads.
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSaveSettings}
                  disabled={loading}
                  className="btn-red w-full sm:w-auto h-12 px-8"
                  data-testid="save-settings-button"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                  GUARDAR CONFIGURACIÓN
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#FF3B30]" />
                  Mensajes de contacto ({messages.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <p className="text-[#8A8A8D] text-center py-8">No hay mensajes de contacto</p>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-4 rounded-lg border transition-colors ${msg.read ? 'bg-[#1C1C1E] border-[rgba(138,138,141,0.1)]' : 'bg-[#1C1C1E] border-[#FF3B30]/30'}`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-white font-medium">{msg.name}</span>
                              {!msg.read && <Badge className="bg-[#FF3B30] text-white text-xs">Nuevo</Badge>}
                            </div>
                            <p className="text-[#8A8A8D] text-sm mb-2">{msg.email}</p>
                            <p className="text-[#C4C4C6] text-sm whitespace-pre-wrap">{msg.message}</p>
                            <p className="text-[#8A8A8D] text-xs mt-2">{new Date(msg.created_at).toLocaleString("es-ES")}</p>
                          </div>
                          {!msg.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#8A8A8D] hover:text-white shrink-0"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/messages/${msg.id}/read`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  setMessages(prev => prev.map(m => m.id === msg.id ? {...m, read: true} : m));
                                } catch { toast.error("Error al marcar como leído"); }
                              }}
                              data-testid={`mark-read-${msg.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Leído
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <Card className="bg-[#111111] border-[rgba(138,138,141,0.15)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white text-lg">Vehículos en Stock ({mktVehicles.length})</CardTitle>
                <Button
                  onClick={() => {
                    setVehicleForm({ title: "", make: "", model: "", year: 2024, kilometers: 0, fuel_type: "Gasolina", power_cv: 0, price: 0, color: "", location: "", notes: "" });
                    setEditingVehicle("new");
                  }}
                  className="btn-red h-9 text-sm"
                  data-testid="add-vehicle-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />Nuevo vehículo
                </Button>
              </CardHeader>
              <CardContent>
                {/* Import from mobile.de */}
                <div className="bg-[#1A1A1A] rounded-xl p-4 mb-6 border border-[rgba(255,59,48,0.15)]" data-testid="import-from-mobile">
                  <h4 className="text-white font-bold mb-3 flex items-center gap-2"><ExternalLink className="w-4 h-4 text-[#FF3B30]" /> Importar desde mobile.de</h4>
                  <div className="flex gap-2">
                    <Input 
                      value={importUrl} 
                      onChange={e => setImportUrl(e.target.value)} 
                      className="glass-input h-10 flex-1" 
                      placeholder="https://www.mobile.de/..." 
                      data-testid="import-url-input"
                    />
                    <Button 
                      className="btn-red h-10 px-5 whitespace-nowrap" 
                      disabled={importing || !importUrl.includes("mobile.de")}
                      data-testid="import-url-btn"
                      onClick={async () => {
                        setImporting(true);
                        try {
                          const res = await axios.post(`${API}/admin/vehicles/import-from-mobile`, { url: importUrl }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(`Vehículo importado con ${res.data.images_downloaded} de ${res.data.images_found || res.data.images_downloaded} fotos`);
                          setImportUrl("");
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al importar");
                        } finally {
                          setImporting(false);
                        }
                      }}
                    >
                      {importing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importando...</> : <><ExternalLink className="w-4 h-4 mr-1" /> Importar</>}
                    </Button>
                  </div>
                  <p className="text-[#636366] text-xs mt-2">Pega el enlace del anuncio de mobile.de. Se descargarán los datos y todas las fotos automáticamente.</p>
                </div>

                {/* Vehicle form */}
                {editingVehicle && (
                  <div className="bg-[#1A1A1A] rounded-xl p-4 mb-6 border border-[rgba(138,138,141,0.1)]" data-testid="vehicle-form">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-bold">{editingVehicle === "new" ? "Nuevo vehículo" : "Editar vehículo"}</h4>
                      <button onClick={() => setEditingVehicle(null)} className="text-[#8A8A8D] hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 lg:col-span-3">
                        <Label className="text-[#C4C4C6] text-xs">Título del anuncio</Label>
                        <Input value={vehicleForm.title} onChange={e => setVehicleForm(f => ({...f, title: e.target.value}))} className="glass-input h-9 mt-1" placeholder="ej: BMW 320d M Sport" data-testid="vf-title" />
                      </div>
                      <div><Label className="text-[#C4C4C6] text-xs">Marca</Label><Input value={vehicleForm.make} onChange={e => setVehicleForm(f => ({...f, make: e.target.value}))} className="glass-input h-9 mt-1" data-testid="vf-make" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Modelo</Label><Input value={vehicleForm.model} onChange={e => setVehicleForm(f => ({...f, model: e.target.value}))} className="glass-input h-9 mt-1" data-testid="vf-model" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Año</Label><Input type="number" value={vehicleForm.year} onChange={e => setVehicleForm(f => ({...f, year: parseInt(e.target.value) || 0}))} className="glass-input h-9 mt-1" data-testid="vf-year" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Kilómetros</Label><Input type="number" value={vehicleForm.kilometers} onChange={e => setVehicleForm(f => ({...f, kilometers: parseInt(e.target.value) || 0}))} className="glass-input h-9 mt-1" data-testid="vf-km" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Combustible</Label>
                        <Select value={vehicleForm.fuel_type} onValueChange={v => setVehicleForm(f => ({...f, fuel_type: v}))}>
                          <SelectTrigger className="glass-input h-9 mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{["Gasolina","Diésel","Híbrido","Eléctrico","GLP"].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-[#C4C4C6] text-xs">Potencia (CV)</Label><Input type="number" value={vehicleForm.power_cv} onChange={e => setVehicleForm(f => ({...f, power_cv: parseInt(e.target.value) || 0}))} className="glass-input h-9 mt-1" data-testid="vf-power" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Precio (€)</Label><Input type="number" value={vehicleForm.price} onChange={e => setVehicleForm(f => ({...f, price: parseInt(e.target.value) || 0}))} className="glass-input h-9 mt-1" data-testid="vf-price" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Color</Label><Input value={vehicleForm.color} onChange={e => setVehicleForm(f => ({...f, color: e.target.value}))} className="glass-input h-9 mt-1" data-testid="vf-color" /></div>
                      <div><Label className="text-[#C4C4C6] text-xs">Ubicación</Label><Input value={vehicleForm.location} onChange={e => setVehicleForm(f => ({...f, location: e.target.value}))} className="glass-input h-9 mt-1" placeholder="ej: Madrid" data-testid="vf-location" /></div>
                      <div className="sm:col-span-2 lg:col-span-3">
                        <Label className="text-[#C4C4C6] text-xs">Notas adicionales</Label>
                        <Textarea value={vehicleForm.notes} onChange={e => setVehicleForm(f => ({...f, notes: e.target.value}))} className="glass-input mt-1 min-h-[60px]" placeholder="Extras, historial, estado..." data-testid="vf-notes" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="outline" onClick={() => setEditingVehicle(null)} className="btn-secondary h-9 text-sm">Cancelar</Button>
                      <Button className="btn-red h-9 text-sm" data-testid="vf-save" onClick={async () => {
                        try {
                          const headers = { Authorization: `Bearer ${token}` };
                          if (editingVehicle === "new") {
                            await axios.post(`${API}/admin/vehicles`, vehicleForm, { headers });
                            toast.success("Vehículo creado");
                          } else {
                            await axios.put(`${API}/admin/vehicles/${editingVehicle}`, vehicleForm, { headers });
                            toast.success("Vehículo actualizado");
                          }
                          setEditingVehicle(null);
                          fetchData();
                        } catch { toast.error("Error al guardar"); }
                      }}><Save className="w-4 h-4 mr-1" />Guardar</Button>
                    </div>
                  </div>
                )}

                {/* Vehicle list */}
                {mktVehicles.length === 0 && !editingVehicle ? (
                  <div className="text-center py-10 text-[#8A8A8D]">
                    <Car className="w-12 h-12 mx-auto mb-3 text-[#636366]" />
                    <p>No hay vehículos en stock</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mktVehicles.map(v => (
                      <div key={v.id} className="bg-[#1A1A1A] rounded-lg p-3 border border-[rgba(138,138,141,0.1)] flex flex-col sm:flex-row gap-3" data-testid={`vehicle-row-${v.id}`}>
                        {/* Thumbnail */}
                        <div className="w-full sm:w-28 h-20 bg-[#111] rounded-md overflow-hidden flex-shrink-0">
                          {v.images?.[0] ? (
                            <img src={`${API}/images/${v.images[0]}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#636366]"><Image className="w-6 h-6" /></div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-white font-bold text-sm truncate">{v.title || `${v.make} ${v.model}`}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                              v.status === 'vendido' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 
                              v.status === 'reservado' ? 'bg-[#FF9500]/20 text-[#FF9500]' : 
                              v.status === 'en_proceso' ? 'bg-[#007AFF]/20 text-[#007AFF]' :
                              'bg-[#34C759]/20 text-[#34C759]'
                            }`}>{v.status === 'vendido' ? 'Vendido' : v.status === 'reservado' ? 'Reservado' : v.status === 'en_proceso' ? 'En proceso' : 'Disponible'}</span>
                          </div>
                          <p className="text-[#8A8A8D] text-xs mt-0.5">{v.year} · {v.kilometers?.toLocaleString()} km · {v.fuel_type} · {v.power_cv} CV</p>
                          <p className="text-[#FF3B30] font-bold mt-1">{v.price?.toLocaleString("es-ES")} €</p>
                        </div>
                        {/* Actions */}
                        <div className="flex sm:flex-col gap-1.5 flex-shrink-0">
                          <Button size="sm" variant="outline" className="text-xs h-7 px-2 border-[rgba(138,138,141,0.2)] text-[#C4C4C6]" onClick={() => {
                            setVehicleForm({ title: v.title, make: v.make, model: v.model, year: v.year, kilometers: v.kilometers, fuel_type: v.fuel_type, power_cv: v.power_cv, price: v.price, color: v.color || "", location: v.location || "", notes: v.notes || "" });
                            setEditingVehicle(v.id);
                          }}>Editar</Button>
                          {/* Status toggle */}
                          <Select value={v.status} onValueChange={async (s) => {
                            try {
                              await axios.put(`${API}/admin/vehicles/${v.id}`, { status: s }, { headers: { Authorization: `Bearer ${token}` } });
                              fetchData();
                            } catch { toast.error("Error"); }
                          }}>
                            <SelectTrigger className="text-xs h-7 px-2 border-[rgba(138,138,141,0.2)] bg-transparent text-[#C4C4C6] w-auto min-w-[90px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="disponible">Disponible</SelectItem>
                              <SelectItem value="en_proceso">En proceso</SelectItem>
                              <SelectItem value="reservado">Reservado</SelectItem>
                              <SelectItem value="vendido">Vendido</SelectItem>
                            </SelectContent>
                          </Select>
                          {/* Image upload */}
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setUploadingImage(true);
                              try {
                                const reader = new FileReader();
                                reader.onload = async (ev) => {
                                  // Compress image
                                  const img = document.createElement('img');
                                  img.onload = async () => {
                                    const canvas = document.createElement('canvas');
                                    const maxW = 1200;
                                    const scale = Math.min(1, maxW / img.width);
                                    canvas.width = img.width * scale;
                                    canvas.height = img.height * scale;
                                    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                                    const compressed = canvas.toDataURL('image/jpeg', 0.8);
                                    await axios.post(`${API}/admin/vehicles/${v.id}/images`, { image: compressed }, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success("Imagen subida");
                                    setUploadingImage(false);
                                    fetchData();
                                  };
                                  img.src = ev.target.result;
                                };
                                reader.readAsDataURL(file);
                              } catch { toast.error("Error al subir imagen"); setUploadingImage(false); }
                              e.target.value = '';
                            }} />
                            <span className="flex items-center gap-1 text-xs h-7 px-2 border border-[rgba(138,138,141,0.2)] rounded-md text-[#C4C4C6] hover:bg-[#222]">
                              {uploadingImage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Foto
                            </span>
                          </label>
                          <Button size="sm" variant="outline" className="text-xs h-7 px-2 border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10" onClick={async () => {
                            if (!window.confirm("¿Eliminar este vehículo?")) return;
                            try {
                              await axios.delete(`${API}/admin/vehicles/${v.id}`, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success("Vehículo eliminado");
                              fetchData();
                            } catch { toast.error("Error"); }
                          }}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#F5A623]" />
                  {editingPost ? "Editar Entrada" : "Nueva Entrada"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#8A8A8D]">Título</Label>
                      <Input className="glass-input h-10" value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} placeholder="Título de la entrada" data-testid="blog-title-input" />
                    </div>
                    <div>
                      <Label className="text-[#8A8A8D]">Imagen de portada (URL)</Label>
                      <Input className="glass-input h-10" value={blogForm.cover_image} onChange={e => setBlogForm({...blogForm, cover_image: e.target.value})} placeholder="https://..." data-testid="blog-cover-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#8A8A8D]">Extracto</Label>
                    <Textarea className="glass-input" value={blogForm.excerpt} onChange={e => setBlogForm({...blogForm, excerpt: e.target.value})} placeholder="Breve resumen de la entrada..." rows={2} />
                  </div>
                  <div>
                    <Label className="text-[#8A8A8D]">Etiquetas (separadas por coma)</Label>
                    <Input className="glass-input h-10" value={blogForm.tags} onChange={e => setBlogForm({...blogForm, tags: e.target.value})} placeholder="importación, alemania, ahorro" />
                  </div>
                  <div>
                    <Label className="text-[#8A8A8D] mb-2 block">Contenido</Label>
                    <RichEditor value={blogForm.content} onChange={val => setBlogForm({...blogForm, content: val})} />
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={blogForm.published} onChange={e => setBlogForm({...blogForm, published: e.target.checked})} className="w-4 h-4 accent-[#F5A623]" />
                      <span className="text-[#8A8A8D] text-sm">Publicar</span>
                    </label>
                    <div className="flex gap-2">
                      {editingPost && (
                        <Button variant="outline" className="btn-secondary" onClick={() => { setEditingPost(null); setBlogForm({ title: "", excerpt: "", content: "", cover_image: "", tags: "", published: false }); }}>
                          Cancelar
                        </Button>
                      )}
                      <Button className="bg-[#F5A623] hover:bg-[#D4901E] text-white" data-testid="blog-save-btn" onClick={async () => {
                        if (!blogForm.title.trim()) { toast.error("El título es obligatorio"); return; }
                        try {
                          const payload = { ...blogForm, tags: blogForm.tags.split(",").map(t => t.trim()).filter(Boolean) };
                          if (editingPost) {
                            await axios.put(`${API}/admin/blog/${editingPost.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success("Entrada actualizada");
                          } else {
                            await axios.post(`${API}/admin/blog`, payload, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success("Entrada creada");
                          }
                          setEditingPost(null);
                          setBlogForm({ title: "", excerpt: "", content: "", cover_image: "", tags: "", published: false });
                          fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
                      }}>
                        <Save className="w-4 h-4 mr-1" /> {editingPost ? "Actualizar" : "Crear entrada"}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Blog posts list */}
                <div className="border-t border-[rgba(138,138,141,0.15)] pt-6">
                  <h4 className="text-white font-bold mb-4">Entradas ({blogPosts.length})</h4>
                  {blogPosts.length === 0 ? (
                    <p className="text-[#636366] text-center py-8">No hay entradas de blog todavía</p>
                  ) : (
                    <div className="space-y-3">
                      {blogPosts.map(post => (
                        <div key={post.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl p-4 border border-[rgba(138,138,141,0.1)]">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {post.cover_image && <img src={post.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-white font-medium truncate">{post.title}</p>
                              <p className="text-[#636366] text-xs">{new Date(post.created_at).toLocaleDateString('es-ES')}</p>
                            </div>
                            <Badge className={post.published ? "bg-[#34C759]/20 text-[#34C759] ml-2" : "bg-[#636366]/20 text-[#636366] ml-2"}>
                              {post.published ? "Publicado" : "Borrador"}
                            </Badge>
                          </div>
                          <div className="flex gap-2 ml-3">
                            <Button size="sm" variant="outline" className="btn-secondary" onClick={() => {
                              setEditingPost(post);
                              setBlogForm({ title: post.title, excerpt: post.excerpt || "", content: post.content || "", cover_image: post.cover_image || "", tags: (post.tags || []).join(", "), published: post.published });
                            }}>
                              <FileText className="w-3 h-3 mr-1" /> Editar
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={async () => {
                              if (!window.confirm("¿Eliminar esta entrada?")) return;
                              try {
                                await axios.delete(`${API}/admin/blog/${post.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success("Entrada eliminada");
                                fetchData();
                              } catch { toast.error("Error al eliminar"); }
                            }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#F5A623]" />
                  Nueva Reseña
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#8A8A8D]">Nombre del cliente</Label>
                      <Input className="glass-input h-10" value={reviewForm.name} onChange={e => setReviewForm({...reviewForm, name: e.target.value})} placeholder="Juan Pérez" data-testid="review-name-input" />
                    </div>
                    <div>
                      <Label className="text-[#8A8A8D]">Foto del cliente (URL, opcional)</Label>
                      <Input className="glass-input h-10" value={reviewForm.photo} onChange={e => setReviewForm({...reviewForm, photo: e.target.value})} placeholder="https://..." />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#8A8A8D]">Reseña</Label>
                    <Textarea className="glass-input" value={reviewForm.text} onChange={e => setReviewForm({...reviewForm, text: e.target.value})} placeholder="Su experiencia con Te Lo Bajo..." rows={3} />
                  </div>
                  <div>
                    <Label className="text-[#8A8A8D] mb-2 block">Estrellas</Label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(s => (
                        <button key={s} type="button" onClick={() => setReviewForm({...reviewForm, stars: s})} className="p-1">
                          <Star className={`w-6 h-6 ${s <= reviewForm.stars ? 'text-[#F5A623] fill-[#F5A623]' : 'text-[#636366]'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button className="bg-[#F5A623] hover:bg-[#D4901E] text-white" data-testid="review-save-btn" onClick={async () => {
                    if (!reviewForm.name.trim() || !reviewForm.text.trim()) { toast.error("Nombre y texto son obligatorios"); return; }
                    try {
                      await axios.post(`${API}/admin/reviews`, reviewForm, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success("Reseña añadida");
                      setReviewForm({ name: "", text: "", stars: 5, photo: "" });
                      fetchData();
                    } catch { toast.error("Error al guardar"); }
                  }}>
                    <Plus className="w-4 h-4 mr-1" /> Añadir reseña
                  </Button>
                </div>

                <div className="border-t border-[rgba(138,138,141,0.15)] pt-6">
                  <h4 className="text-white font-bold mb-4">Reseñas ({reviews.length})</h4>
                  {reviews.length === 0 ? (
                    <p className="text-[#636366] text-center py-8">No hay reseñas todavía</p>
                  ) : (
                    <div className="space-y-3">
                      {reviews.map(r => (
                        <div key={r.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl p-4 border border-[rgba(138,138,141,0.1)]">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {r.photo ? (
                              <img src={r.photo} alt={r.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-[#F5A623]/15 flex items-center justify-center text-[#F5A623] font-bold flex-shrink-0">{r.name?.charAt(0)?.toUpperCase()}</div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{r.name}</p>
                                <div className="flex gap-0.5">
                                  {[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.stars ? 'text-[#F5A623] fill-[#F5A623]' : 'text-[#636366]'}`} />)}
                                </div>
                              </div>
                              <p className="text-[#8A8A8D] text-xs truncate">{r.text}</p>
                            </div>
                          </div>
                          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 ml-3" onClick={async () => {
                            if (!window.confirm("¿Eliminar esta reseña?")) return;
                            try {
                              await axios.delete(`${API}/admin/reviews/${r.id}`, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success("Reseña eliminada");
                              fetchData();
                            } catch { toast.error("Error al eliminar"); }
                          }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedLead(null)}
        >
          <div 
            className="glass-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Detalle del Lead</h3>
              <button 
                onClick={() => setSelectedLead(null)}
                className="text-[#8A8A8D] hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#8A8A8D] text-sm">Nombre</p>
                  <p className="text-white font-medium">{selectedLead.name}</p>
                </div>
                <div>
                  <p className="text-[#8A8A8D] text-sm">Tipo</p>
                  <p className="text-white font-medium capitalize">{selectedLead.buyer_type}</p>
                </div>
                <div>
                  <p className="text-[#8A8A8D] text-sm">Email</p>
                  <p className="text-white">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-[#8A8A8D] text-sm">Teléfono</p>
                  <p className="text-white">{selectedLead.phone}</p>
                </div>
                <div>
                  <p className="text-[#8A8A8D] text-sm">Provincia</p>
                  <p className="text-white">{selectedLead.province}</p>
                </div>
                <div>
                  <p className="text-[#8A8A8D] text-sm">Fecha</p>
                  <p className="text-white">{formatDate(selectedLead.created_at)}</p>
                </div>
              </div>
              
              {selectedLead.vehicle_data?.url && (
                <div>
                  <p className="text-[#8A8A8D] text-sm mb-1">Enlace mobile.de</p>
                  <a 
                    href={selectedLead.vehicle_data.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#FF3B30] hover:underline flex items-center gap-1 text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver anuncio original
                  </a>
                </div>
              )}
              
              <div className="bg-[#1A1A1A] rounded-xl p-4 border border-[rgba(138,138,141,0.1)]">
                <p className="text-[#8A8A8D] text-sm mb-2">Vehículo</p>
                <p className="text-white font-medium">{selectedLead.vehicle_data?.title}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-[#8A8A8D]">
                  <span>{selectedLead.vehicle_data?.year}</span>
                  <span>•</span>
                  <span>{selectedLead.vehicle_data?.kilometers?.toLocaleString()} km</span>
                  <span>•</span>
                  <span>{selectedLead.vehicle_data?.fuel_type}</span>
                  {selectedLead.vehicle_data?.co2_emissions > 0 && (
                    <>
                      <span>•</span>
                      <span>{selectedLead.vehicle_data?.co2_emissions} g/km CO₂</span>
                    </>
                  )}
                </div>
                <p className="text-[#FF3B30] font-bold text-lg mt-2">
                  Precio vehículo: {formatPrice(selectedLead.vehicle_data?.price || 0)}
                </p>
              </div>
              
              <div className="breakdown-total">
                <p className="text-lg text-white font-bold mb-2">Total Calculado</p>
                <p className="text-3xl text-[#FF3B30] font-bold">
                  {formatPrice(selectedLead.calculated_result?.total || 0)}
                </p>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-[#C4C4C6] text-sm">Notas internas</Label>
                <Textarea
                  className="glass-input mt-1 min-h-[80px]"
                  placeholder="Añade notas sobre este lead..."
                  defaultValue={selectedLead.notes || ""}
                  onBlur={(e) => updateLeadNotes(selectedLead.id, e.target.value)}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => deleteLead(selectedLead.id)}
                  variant="outline"
                  className="flex-1 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Lead
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Detail Modal */}
      {selectedSearch && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedSearch(null)}
        >
          <div 
            className="glass-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Detalle de Búsqueda</h3>
              <button 
                onClick={() => setSelectedSearch(null)}
                className="text-[#8A8A8D] hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-[#8A8A8D] text-sm">Fecha</p>
                <p className="text-white">{formatDate(selectedSearch.created_at)}</p>
              </div>

              <div>
                <p className="text-[#8A8A8D] text-sm mb-1">URL de mobile.de</p>
                <a 
                  href={selectedSearch.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#FF3B30] hover:underline flex items-center gap-1 text-sm break-all"
                >
                  <ExternalLink className="w-4 h-4 flex-shrink-0" />
                  {selectedSearch.url}
                </a>
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <p className="text-[#8A8A8D] text-sm">Scraping</p>
                  {selectedSearch.scrape_success ? (
                    <Badge className="bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30 border mt-1">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Exitoso
                    </Badge>
                  ) : (
                    <Badge className="bg-[#FF9500]/20 text-[#FF9500] border-[#FF9500]/30 border mt-1">
                      <XCircle className="w-3 h-3 mr-1" />
                      Manual requerido
                    </Badge>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-[#8A8A8D] text-sm">Conversión</p>
                  {selectedSearch.converted_to_lead ? (
                    <Badge className="bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30 border mt-1">
                      Convertido a Lead
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 border mt-1">
                      No convertido
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="bg-[#1A1A1A] rounded-xl p-4 border border-[rgba(138,138,141,0.1)]">
                <p className="text-[#8A8A8D] text-sm mb-2">Datos Extraídos del Vehículo</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#8A8A8D]">Título:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.title || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">Precio:</span>
                    <span className="text-[#FF3B30] ml-2 font-medium">
                      {selectedSearch.vehicle_data?.price > 0 ? formatPrice(selectedSearch.vehicle_data.price) : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">Año:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.year || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">Kilómetros:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.kilometers?.toLocaleString() || '-'} km</span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">Combustible:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.fuel_type || '-'}</span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">Potencia:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.power_cv || '-'} CV</span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">CO₂:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.co2_emissions || '-'} g/km</span>
                  </div>
                  <div>
                    <span className="text-[#8A8A8D]">Marca:</span>
                    <span className="text-white ml-2">{selectedSearch.vehicle_data?.make || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => deleteSearch(selectedSearch.id)}
                  variant="outline"
                  className="flex-1 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Búsqueda
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
